import React from 'react';
import { useScrollReveal } from './useScrollReveal';
import WhatsAppButton from './WhatsAppButton';
import StickyHeader from './StickyHeader';

const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || 'https://agenda.filtrosyaceitesjm.com/login';

const TemplateElegant = ({ data, config }) => {
  useScrollReveal();

  const activeServices = Array.isArray(data?.services) && data.services.length > 0
    ? config.services.filter(s => data.services.includes(s.id))
    : config.services.slice(0, 6);

  const primary = data.brand_color_primary || '#1e293b';
  const secondary = data.brand_color_secondary || '#a855f7';
  
  const finalBookingUrl = `${data.booking_url || BOOKING_URL}?workshopId=${data.id}&niche=${data.niche}`;

  return (
    <div className="min-h-screen bg-[#faf8f5] text-gray-800 font-serif">
      <StickyHeader name={data.name || 'Mi Taller'} brandColor={primary} bookingUrl={finalBookingUrl} />
      <WhatsAppButton phone={data.whatsapp} name={data.name || 'Taller'} />

      {/* Hero Section */}
      <header className="relative min-h-[80vh] flex items-center">
        <div className="absolute inset-0 w-full h-full">
          <img src={data.hero_image_url || config.images[0].url} alt="Hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          {/* Social Proof Badge */}
          <div className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/50 border border-black/5 backdrop-blur-md mb-8 shadow-sm">
            <div className="flex gap-1 text-yellow-500 text-sm">
              <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
            </div>
            <span className="text-gray-800 text-xs font-bold uppercase tracking-widest border-l border-black/10 pl-2 ml-1">Premium</span>
          </div>

          <h1 className="reveal delay-100 text-5xl md:text-7xl font-light mb-6 text-gray-900 tracking-tight">
            {data.hero_title || data.name || config.defaultTitle}
          </h1>
          <div className="reveal delay-200 w-24 h-1 mx-auto mb-6" style={{ backgroundColor: secondary }}></div>
          <p className="reveal delay-300 text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto tracking-wide">
            {data.hero_description || config.defaultDescription}
          </p>
          <button
            className="reveal delay-400 group px-10 py-4 rounded-none uppercase tracking-widest text-sm transition-all duration-500 font-semibold flex items-center justify-center gap-3 mx-auto hover:shadow-2xl hover:-translate-y-1"
            style={{ border: `1px solid ${primary}`, color: '#ffffff', backgroundColor: primary }}
            onClick={() => { window.location.href = finalBookingUrl; }}
          >
            Reservar Experiencia
            <svg className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </button>
        </div>
      </header>

      {/* Services Section */}
      <section className="py-24 px-6 relative overflow-hidden" style={{ background: `linear-gradient(135deg, #f5f0ff 0%, #faf8f5 50%, #f0f4ff 100%)` }}>
        {/* Blobs decorativos */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20 blur-3xl -translate-y-1/2 translate-x-1/3" style={{ backgroundColor: secondary }}></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10 blur-3xl translate-y-1/2 -translate-x-1/4" style={{ backgroundColor: primary }}></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="reveal text-center mb-16">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Servicios Exclusivos</h2>
            <div className="w-16 h-px mx-auto" style={{ backgroundColor: secondary }}></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeServices.map((service, index) => (
              <div
                key={index}
                className="reveal group relative p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                style={{
                  background: 'rgba(255,255,255,0.65)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: `1px solid rgba(255,255,255,0.8)`,
                  boxShadow: `0 10px 40px -10px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,1), inset 0 0 20px rgba(255,255,255,0.5)`,
                  transitionDelay: `${(index % 3) * 100}ms`
                }}
              >
                {/* Shine and glow effects */}
                <div className="absolute inset-0 rounded-3xl pointer-events-none transition-opacity duration-500 opacity-50 group-hover:opacity-100" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, transparent 50%)' }}></div>
                <div className="absolute top-0 left-8 right-8 h-px transition-all duration-500 group-hover:left-4 group-hover:right-4" style={{ background: `linear-gradient(to right, transparent, ${secondary}80, transparent)` }}></div>

                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-2xl transition-all duration-500 group-hover:scale-110 group-hover:shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${secondary}25, ${secondary}10)`,
                    border: `1px solid ${secondary}30`,
                  }}
                >
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold capitalize mb-3 text-gray-900 group-hover:text-black transition-colors">{service.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed group-hover:text-gray-700 transition-colors">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pt-16 pb-8 px-6 bg-gray-900 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 mb-12">
            
            {/* 1. Marca y Descripción */}
            <div className="reveal md:col-span-4 flex flex-col">
              <h3 className="text-white font-light text-2xl mb-1" style={{ fontFamily: 'serif' }}>{data.name}</h3>
              <div className="w-12 h-px mb-5" style={{ backgroundColor: secondary }}></div>
              {data.footer_tagline && (
                <p className="text-gray-400 text-sm leading-relaxed mb-6 italic">{data.footer_tagline}</p>
              )}
              <div className="flex flex-wrap gap-2.5 mt-auto">
                {data.whatsapp && (
                  <a href={`https://wa.me/${data.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-white text-xs font-bold transition-opacity hover:opacity-80"
                    style={{ backgroundColor: '#25D366' }}>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.532 5.855L.057 23.885a.75.75 0 0 0 .92.92l6.104-1.464A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.696 9.696 0 0 1-4.945-1.355l-.355-.21-3.625.869.883-3.538-.23-.367A9.697 9.697 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/></svg>
                    WhatsApp
                  </a>
                )}
                {data.instagram && (
                  <a href={data.instagram} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-white text-xs font-bold transition-opacity hover:opacity-80"
                    style={{ background: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/></svg>
                    Instagram
                  </a>
                )}
                {data.facebook && (
                  <a href={data.facebook} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-white text-xs font-bold transition-opacity hover:opacity-80"
                    style={{ backgroundColor: '#1877F2' }}>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                    Facebook
                  </a>
                )}
                {data.tiktok && (
                  <a href={data.tiktok} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-white text-xs font-bold transition-opacity hover:opacity-80"
                    style={{ backgroundColor: '#010101', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/></svg>
                    TikTok
                  </a>
                )}
              </div>
            </div>

            {/* 2. Info de Contacto */}
            <div className="reveal delay-100 md:col-span-4 flex flex-col">
              <h4 className="text-gray-400 font-bold mb-6 text-sm uppercase tracking-wider">Contacto</h4>
              <ul className="space-y-4 text-sm text-gray-400">
                {data.phone && (
                  <li className="flex items-start gap-3">
                    <span className="text-lg leading-none">📞</span>
                    <span className="mt-0.5">{data.phone}</span>
                  </li>
                )}
                {data.address && (
                  <li className="flex items-start gap-3">
                    <span className="text-lg leading-none">📍</span>
                    <span className="mt-0.5">{data.address}{data.city ? `, ${data.city}` : ''}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* 3. Mapa interactivo */}
            {data.address && (
              <div className="reveal delay-200 md:col-span-4 flex flex-col">
                <h4 className="text-gray-400 font-bold mb-6 text-sm uppercase tracking-wider">Ubicación</h4>
                <div className="w-full h-48 rounded-xl overflow-hidden shadow-2xl relative group" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
                  <iframe
                    title="map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(`${data.address}, ${data.city || ''}`)}&output=embed`}
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            )}
          </div>
          
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs border-t border-white/5 text-gray-500">
            <p>© {new Date().getFullYear()} {data.name}. Todos los derechos reservados.</p>
            <div className="flex gap-6 font-medium">
              <a href="#" className="hover:text-gray-300 transition-colors duration-300">Privacidad</a>
              <a href="#" className="hover:text-gray-300 transition-colors duration-300">Términos</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TemplateElegant;
